import { defineStore } from 'pinia'
import settingsService from '@/services/settings.service'
import type {
  Firm,
  NationalSource,
  NationalSourceInput,
  Profile,
} from '@/services/settings.service'
import notificationsService from '@/services/notifications.service'
import type { Preference } from '@/services/notifications.service'
import type { NotificationChannel, NotificationKind } from '@/entities/notification'
import { isAbortError } from '@/api'
import type { Locale } from '@/i18n'
import { initialsOf } from '@/services/auth.service'
import { useAuthStore } from './auth.store'

/**
 * The settings and account pages (W11): the person's own profile and
 * language, their notification matrix, the firm, and the firm's national
 * registry connections — every part read from and written to the API.
 *
 * What this replaces was one `config` tree from a fixture, with a
 * `patchLocal()` "used by the demo toggles" and a `save()` that sent the whole
 * tree to a `PATCH /settings` that does not exist. Each part here has its own
 * load and error state, because each comes from a different endpoint and one
 * failing must not blank the others (the `Promise.all` trap in `@frontend`).
 */
export interface SettingsState {
  profile: Profile | null
  profileError: unknown
  localeError: unknown
  preferences: Preference[]
  preferencesLoaded: boolean
  preferencesError: unknown
  firm: Firm | null
  firmError: unknown
  sources: NationalSource[]
  sourcesLoaded: boolean
  sourcesError: unknown
}

const cell = (kind: string, channel: string) => `${kind}:${channel}`

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => ({
    profile: null,
    profileError: null,
    localeError: null,
    preferences: [],
    preferencesLoaded: false,
    preferencesError: null,
    firm: null,
    firmError: null,
    sources: [],
    sourcesLoaded: false,
    sourcesError: null,
  }),

  getters: {
    /** `enabled` for one cell of the matrix; on by default, like the API. */
    preference: (state) => (kind: NotificationKind, channel: NotificationChannel) =>
      state.preferences.find((p) => p.kind === kind && p.channel === channel)?.enabled ?? true,
  },

  actions: {
    /* ---------------------------------------------------------- profile */

    async fetchProfile() {
      const auth = useAuthStore()
      if (auth.user.id == null) return
      this.profileError = null
      try {
        this.profile = await settingsService.profile(auth.user.id)
      } catch (err) {
        if (!isAbortError(err)) this.profileError = err
      }
    },

    async saveProfile(patch: { displayName: string }) {
      const auth = useAuthStore()
      if (auth.user.id == null) return
      this.profile = await settingsService.updateProfile(auth.user.id, patch)
      /* The sidebar, topbar and account popup read the session's user. */
      const name = this.profile.displayName
      auth.user = { ...auth.user, name, initials: initialsOf(name) }
    },

    async changePassword(current: string, next: string) {
      const auth = useAuthStore()
      if (auth.user.id == null) return
      await settingsService.changePassword(auth.user.id, current, next)
    },

    /**
     * Store the language on the account, so emails and notifications are
     * written in it (D-026). Called by the language switcher for a signed-in
     * person; the choice already took effect in this browser, so a failure is
     * recorded for the page to show rather than thrown at the switcher.
     */
    async saveLocale(locale: Locale) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated || auth.user.id == null) return
      this.localeError = null
      try {
        this.profile = await settingsService.updateProfile(auth.user.id, { locale })
      } catch (err) {
        this.localeError = err
      }
    },

    /* ---------------------------------------------------------- notifications */

    async fetchPreferences() {
      this.preferencesError = null
      try {
        this.preferences = await notificationsService.preferences()
        this.preferencesLoaded = true
      } catch (err) {
        if (!isAbortError(err)) this.preferencesError = err
      }
    },

    /**
     * Flip one cell. Shown at once, sent at once, and put back if the API
     * refuses; on success the whole matrix is replaced by the API's answer.
     */
    async setPreference(kind: NotificationKind, channel: NotificationChannel, enabled: boolean) {
      const before = this.preferences
      const key = cell(kind, channel)
      const known = before.some((p) => cell(p.kind, p.channel) === key)
      this.preferences = known
        ? before.map((p) =>
            cell(p.kind, p.channel) === key ? { ...p, enabled, explicit: true } : p
          )
        : [...before, { kind, channel, enabled, explicit: true }]
      try {
        this.preferences = await notificationsService.savePreferences([{ kind, channel, enabled }])
      } catch (err) {
        this.preferences = before
        throw err
      }
    },

    /* ---------------------------------------------------------- firm */

    async fetchFirm() {
      this.firmError = null
      try {
        this.firm = await settingsService.firm()
      } catch (err) {
        if (!isAbortError(err)) this.firmError = err
      }
    },

    async saveFirm(patch: { name: string; contactEmail: string | null }) {
      this.firm = await settingsService.updateFirm(patch)
    },

    /* ---------------------------------------------------------- national registries */

    async fetchSources() {
      this.sourcesError = null
      try {
        this.sources = await settingsService.nationalSources()
        this.sourcesLoaded = true
      } catch (err) {
        if (!isAbortError(err)) this.sourcesError = err
      }
    },

    async saveSource(input: NationalSourceInput, existing: NationalSource | null) {
      const saved = await settingsService.saveNationalSource(input, existing)
      this.sources = [
        ...this.sources.filter((s) => s.countryCode !== saved.countryCode),
        saved,
      ].sort((a, b) => a.countryCode.localeCompare(b.countryCode))
      return saved
    },

    async removeSource(countryCode: string) {
      await settingsService.removeNationalSource(countryCode)
      this.sources = this.sources.filter((s) => s.countryCode !== countryCode)
    },
  },
})
